/** */
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useAnalystService } from 'hooks/api/agents/analyst';
import { GridRow, GridColumn } from 'app/member/layouts/grid';
import { Button } from 'app/shared/button';
import { Spacer } from 'app/layouts/generic';
import { SectionCard } from 'app/shared/section-card';
import { LineChart } from 'app/shared/charts/line';
import { StatCard } from 'app/shared/stat-card';
import { formatMonth } from 'utilities/date';
import { fmt } from 'utilities/format';
import styles from './revenue-report.module.css';

const PLAN_COLORS = [
  'var(--blue-primary)',
  'var(--green-primary)',
  'var(--orange-primary)',
  'var(--red-primary)'
];

const CLASS_LABELS = { sub: 'Subscription', credit: 'Credit' };
const CLASS_COLORS = { sub: 'var(--blue-primary)', credit: 'var(--orange-primary)' };

const MRR_LINES = [{ key: 'mrr', label: 'MRR', color: 'var(--blue-primary)' }];

const MRR_MOVEMENT_ROWS = [
  { key: 'new_mrr', label: 'New MRR' },
  { key: 'expansion_mrr', label: 'Expansion MRR' },
  { key: 'reactivation_mrr', label: 'Reactivation MRR' },
  { key: 'contraction_mrr', label: 'Contraction MRR' },
  { key: 'churned_mrr', label: 'Churned MRR' },
  { key: 'paused_mrr', label: 'Paused MRR' }
];

const parseAmount = (value) => parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;

export const RevenueReport = ({ domain = {} }) => {
  const { metrics = {}, tableData = [], mrrHistory = [] } = domain;
  const plan_rows = tableData.filter((r) => r.type === 'plan');
  const chart_data = mrrHistory.map((p) => ({ month: formatMonth(p.month), mrr: p.mrr }));

  const class_rows = Object.values(
    plan_rows.reduce((acc, r) => {
      const key = r.class || 'other';
      if (!acc[key]) acc[key] = { class: key, customers: 0, mrr: 0, pct_mrr: 0 };
      acc[key].customers += Number(r.customers) || 0;
      acc[key].mrr += parseAmount(r.mrr);
      acc[key].pct_mrr += parseAmount(r.pct_mrr);
      return acc;
    }, {})
  );

  const starting_mrr = (metrics.current_mrr?.value ?? 0) - (metrics.net_new_mrr?.value ?? 0);
  const nrr =
    starting_mrr > 0
      ? ((starting_mrr +
          (metrics.expansion_mrr?.value ?? 0) +
          (metrics.reactivation_mrr?.value ?? 0) -
          (metrics.contraction_mrr?.value ?? 0) -
          (metrics.churned_mrr?.value ?? 0)) /
          starting_mrr) *
        100
      : null;

  return (
    <>
      <GridRow num_of_columns={5} auto_flow={false}>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="MRR" metric={metrics.current_mrr} format="currency" />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="Net New MRR" metric={metrics.net_new_mrr} format="currency" />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="MRR Growth" metric={metrics.mrr_growth_rate} format="percent" />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard
            title="Revenue Churn"
            metric={metrics.revenue_churn_rate}
            format="percent"
            invert
          />
        </GridColumn>
        <GridColumn span={1} auto_columns={false}>
          <StatCard title="ARPU" metric={metrics.arpu} format="currency" />
        </GridColumn>
      </GridRow>

      <Spacer multiple={4} />

      <GridRow num_of_columns={5} auto_flow={false}>
        <GridColumn span={3} auto_columns={false}>
          <SectionCard title="MRR Growth — Last 13 Months">
            {chart_data.length === 0 ? (
              <div className={styles.empty}>No monthly history available yet</div>
            ) : (
              <div className={styles.chartWrap}>
                <LineChart data={chart_data} lines={MRR_LINES} xKey="month" rotateLabels />
              </div>
            )}
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="Plan Breakdown">
            {plan_rows.length === 0 ? (
              <div className={styles.empty}>No plan data</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Customers</th>
                    <th>% Customers</th>
                    <th>MRR</th>
                    <th>% MRR</th>
                    <th>ARPU</th>
                  </tr>
                </thead>
                <tbody>
                  {plan_rows.map((r, i) => (
                    <tr key={i}>
                      <td className={styles.planName}>
                        <span className={styles.planDot} style={{ background: PLAN_COLORS[i] }} />
                        {r.plan}
                      </td>
                      <td>{r.customers}</td>
                      <td>{r.pct_customers}</td>
                      <td className={styles.amount}>{r.mrr}</td>
                      <td>{r.pct_mrr}</td>
                      <td className={styles.amount}>{r.arpu}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </GridColumn>

        <GridColumn span={2} auto_columns={false}>
          <SectionCard title="Revenue by Class">
            {class_rows.length === 0 ? (
              <div className={styles.empty}>No class data</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Customers</th>
                    <th>MRR</th>
                    <th>% MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {class_rows.map((r) => (
                    <tr key={r.class}>
                      <td className={styles.planName}>
                        <span
                          className={styles.planDot}
                          style={{ background: CLASS_COLORS[r.class] || 'var(--neutral-dark-4)' }}
                        />
                        {CLASS_LABELS[r.class] || r.class}
                      </td>
                      <td>{r.customers}</td>
                      <td className={styles.amount}>{fmt(r.mrr, 'currency')}</td>
                      <td>{fmt(r.pct_mrr, 'percent')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <Spacer multiple={4} />

          <SectionCard title="MRR Movement">
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {MRR_MOVEMENT_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className={styles.amount}>{fmt(metrics[row.key]?.value, 'currency')}</td>
                  </tr>
                ))}
                <tr>
                  <td className={styles.bold}>Net Revenue Retention</td>
                  <td className={cx(styles.amount, styles.bold)}>{fmt(nrr, 'percent')}</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </GridColumn>
      </GridRow>
    </>
  );
};

export const RevenueReportPage = () => {
  const { fetchDashboard, triggerRun } = useAnalystService();
  const [revenue_domain, setRevenueDomain] = useState({});
  const [running, setRunning] = useState(false);

  const handleRunAnalysis = async () => {
    setRunning(true);
    await triggerRun('revenue');
    setRunning(false);
  };

  useEffect(() => {
    fetchDashboard().then(({ dashboard }) => setRevenueDomain(dashboard?.domains?.revenue || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <GridRow num_of_columns={12} auto_flow={false}>
        <GridColumn span={9} auto_columns={false} />
        <GridColumn span={3} auto_columns={false}>
          <Button
            text="Run Revenue Analysis"
            type="secondary"
            loading={running}
            onClick={handleRunAnalysis}
          />
        </GridColumn>
      </GridRow>
      <Spacer multiple={4} />
      <RevenueReport domain={revenue_domain} />
    </>
  );
};
